import { css } from '@repo/pandacss/styled-system/css';
import { flex, stack } from '@repo/pandacss/styled-system/patterns';
import { Skeleton } from '../../../Skeleton';

export type ProductCardSkeletonProps = {
  ourTakeExpanded?: boolean;
};

export function ProductCardSkeleton({ ourTakeExpanded = false }: ProductCardSkeletonProps) {
  return (
    <div
      className={css({
        borderTopWidth: 'medium',
        borderRightWidth: 'medium',
        borderBottomWidth: 'medium',
        borderLeftWidth: 'medium',
        borderTopStyle: 'solid',
        borderRightStyle: 'solid',
        borderBottomStyle: 'solid',
        borderLeftStyle: 'solid',
        borderTopColor: 'neutral.lighter',
        borderRightColor: 'neutral.lighter',
        borderBottomColor: 'neutral.lighter',
        borderLeftColor: 'neutral.lighter',
      })}
    >
      <div
        className={css({
          paddingBlockStart: '4',
          paddingBlockEnd: '4',
          paddingInlineStart: { base: '4', md: '7' },
          paddingInlineEnd: { base: '4', md: '7' },
        })}
      >
        {/* Title */}
        <div className={css({ marginBottom: '4' })}>
          <Skeleton variant="rounded" width={250} height={40} />
        </div>

        {/* Main Content */}
        <div
          className={flex({
            direction: { base: 'column', md: 'row' },
          })}
        >
          <div
            className={flex({
              direction: { base: 'column', md: 'row' },
              columnGap: '4',
              rowGap: '4',
              width: { base: 'full', md: 'auto' },
              marginRight: { base: '0', md: '12' },
            })}
          >
            {/* Image and CTA */}
            <div className={css({ width: { base: 'full', md: '[200px]' } })}>
              <Skeleton variant="rounded" width="100%" height={90} />
              <Skeleton
                variant="rounded"
                width="100%"
                height={60}
                className={css({ marginTop: '2' })}
              />
            </div>

            {/* Rating Section */}
            <div
              className={css({
                flexDirection: 'column',
                columnGap: '2',
                rowGap: '2',
                width: { base: 'full', md: 'auto' },
                display: { base: 'none', md: 'flex' },
              })}
            >
              <Skeleton variant="rounded" width={200} height={30} />
              <Skeleton variant="rounded" width={180} height={40} />
            </div>
          </div>

          {/* Drivers */}
          <div
            className={stack({
              direction: 'column',
              marginTop: { base: '4', md: '0' },
              flexGrow: '1',
              flexShrink: '1',
              flexBasis: '[0%]',
              columnGap: '2',
              rowGap: '2',
            })}
          >
            {[...Array(4)].map((_, index) => (
              <div
                key={index}
                className={flex({
                  align: 'center',
                  borderBottomWidth: 'thin',
                  borderBottomStyle: 'solid',
                  borderBottomColor: 'neutral.lighter',
                  paddingBottom: '2',
                  '&:last-of-type': {
                    borderBottomWidth: 'none',
                    borderBottomStyle: 'none',
                    borderBottomColor: 'transparent',
                    paddingBottom: '0',
                  },
                })}
              >
                <div
                  className={css({
                    display: 'flex',
                    flexBasis: '[25%]',
                    columnGap: '1',
                    rowGap: '1',
                  })}
                >
                  <Skeleton variant="rounded" width={150} height={30} />
                </div>
                <Skeleton
                  variant="rounded"
                  width={100}
                  height={30}
                  className={css({ marginLeft: '4' })}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Our Take */}
      <div
        className={css({
          borderTopWidth: 'thin',
          borderTopStyle: 'solid',
          borderTopColor: 'neutral.lighter',
        })}
      >
        <div
          className={flex({
            align: 'center',
            columnGap: '1',
            rowGap: '1',
            marginBottom: '2',
            height: '12',
            paddingInlineStart: '4',
            paddingInlineEnd: '4',
            paddingBlockStart: '2',
            paddingBlockEnd: '2',
          })}
        >
          <Skeleton variant="rounded" width={150} height={20} />
        </div>
        {ourTakeExpanded && (
          <div
            className={stack({
              direction: 'column',
              paddingTop: '4',
              paddingRight: '4',
              paddingBottom: '4',
              paddingLeft: '4',
              columnGap: '4',
              rowGap: '4',
              height: '[500px]',
            })}
          >
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton
                key={`our-take-skeleton-${index}`}
                variant="rounded"
                width="100%"
                height={80}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
